import { Spinner, useToast } from '@chakra-ui/react'
import {
  faTriangleExclamation,
  faXmark,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { skipToken } from '@reduxjs/toolkit/query'
import { captureException } from '@sentry/react'
import { useEffect, useState } from 'react'
import CDropdownMenu from '../../../../../../components/CDropdownMenu'
import DefaultModal from '../../../../../../components/DefaultModal'
import DropdownCell from '../../../../../../components/DropdownCell'
import InfoBox from '../../../../../../components/InfoBox'
import Toast from '../../../../../../components/Toast'
import {
  useGetBankAccountsQuery,
  useGetExchangeRatePairQuery,
  useGetNetworksQuery,
  useGetWalletsQuery,
  useUpdateCurrencyMutation,
} from '../../../../../../redux/services/coreApi'
import type {
  CurrencyToOffRampDto,
  NetworkOffRampDto,
} from '../../../../../../types/coreApi-types'
import ImageAndValue from './ImageAndValue'

interface CurrenciesScreenProps {
  isOpen: boolean
  onClose: () => void
  currency?: CurrencyToOffRampDto | null
}

const CurrencyOverviewDetailsModal: React.FC<CurrenciesScreenProps> = ({
  isOpen,
  onClose,
  currency,
}) => {
  const toast = useToast()

  const [active, setActive] = useState(false)
  const [updateCurrency, { isLoading: updateCurrencyIsLoading }] =
    useUpdateCurrencyMutation()
  const {
    data: exchangeRateData,
    isFetching: exchangeRateFetching,
    isError: exchangeRateIsError,
  } = useGetExchangeRatePairQuery(
    currency
      ? {
          currencyFromIsoCode: currency?.isoCode,
          currencyToIsoCode: 'EUR',
        }
      : skipToken,
    { skip: !currency }
  )
  const { data: networks } = useGetNetworksQuery()
  const { data: wallets } = useGetWalletsQuery(null)
  const { data: bankAccounts } = useGetBankAccountsQuery(null)

  const [activeNetworks, setActiveNetworks] = useState<
    { id: string; name: string }[]
  >([])
  const [activeWallets, setActiveWallets] = useState<
    { id: string; address: string; network: NetworkOffRampDto }[]
  >([])
  const [activeOutboundWallets, setActiveOutboundWallets] = useState<
    { id: string; address: string; network: NetworkOffRampDto }[]
  >([])
  const [selectedOutboundBankAccount, setSelectedOutboundBankAccount] =
    useState<{ id: string; name: string; description?: string | null } | null>(
      null
    )
  const [selectedSystemBankAccount, setSelectedSystemBankAccount] = useState<{
    id: string
    name: string
    description?: string | null
  } | null>(null)

  useEffect(() => {
    if (currency) {
      setActive((currency.active as unknown) === 'Active' ? true : false)
      setActiveNetworks(currency.networks || [])
      setActiveWallets(currency.supportedSystemWallets || [])
      setActiveOutboundWallets(currency.outboundSystemWallets || [])
      setSelectedOutboundBankAccount(currency.outboundSystemBankAccount || null)
      setSelectedSystemBankAccount(currency.systemBankAccount || null)
    }
  }, [currency, setActiveNetworks])

  return (
    <DefaultModal
      isLoading={false}
      isOpen={isOpen}
      onClose={onClose}
      whiteBG
      maxW="700px"
      title="Currency Details"
    >
      <div className="p-6 space-y-4">
        {/* Exchange Rate Section */}
        <div className="bg-gradient-to-r from-brand-accent/5 to-brand-accent/10 rounded-xl p-4 border border-brand-accent/20">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-brand-light-text-description dark:text-brand-dark-text-description mb-1">
                Exchange Rate
              </h3>
              {exchangeRateFetching ? (
                <div className="flex items-center gap-2">
                  <Spinner size="sm" />
                  <span className="text-sm text-brand-light-text-subtle-description">
                    Loading rate...
                  </span>
                </div>
              ) : exchangeRateIsError ? (
                <div className="flex items-center gap-2 text-brand-light-red">
                  <FontAwesomeIcon
                    icon={faTriangleExclamation}
                    className="text-sm"
                  />
                  <span className="text-sm">Unable to fetch rate</span>
                </div>
              ) : (
                <p className="text-lg font-semibold text-brand-light-text-default dark:text-brand-dark-text-default">
                  1 {currency?.isoCode} ≈ {exchangeRateData?.exchangeRate} €
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Currency Header Card */}
        <div className="card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <ImageAndValue
                value={currency?.name ?? ''}
                iconUrl={currency?.iconUrl}
              />
              <div>
                <p className="text-sm text-brand-light-text-description dark:text-brand-dark-text-description">
                  {currency?.isoCode}
                </p>
                <p className="text-xs text-brand-light-text-subtle-description dark:text-brand-dark-text-subtle-description">
                  {currency?.currencyType}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {active ? (
                <InfoBox colorScheme="green" className="px-3 py-1.5">
                  <div className="w-2 h-2 bg-current rounded-full mr-2"></div>
                  Active
                </InfoBox>
              ) : (
                <InfoBox colorScheme="red" className="px-3 py-1.5">
                  <div className="w-2 h-2 bg-current rounded-full mr-2"></div>
                  Inactive
                </InfoBox>
              )}
            </div>
          </div>

          <button
            className="mt-4 button-secondary text-sm py-2"
            onClick={async () => {
              if (!currency) return
              setActive(!active)
            }}
          >
            {updateCurrencyIsLoading ? (
              <div className="flex items-center gap-2">
                <Spinner size="xs" />
                Updating...
              </div>
            ) : (
              `Mark as ${active ? 'Inactive' : 'Active'}`
            )}
          </button>
        </div>

        {currency?.currencyType === 'CryptoCurrency' && (
          <div className="space-y-4">
            {/* Networks Section */}
            <div className="card">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-base font-semibold text-brand-light-text-default dark:text-brand-dark-text-default">
                  Supported Networks
                </h3>
                <span className="text-xs text-brand-light-text-subtle-description dark:text-brand-dark-text-subtle-description bg-brand-light-card-border dark:bg-brand-dark-card-border px-2 py-1 rounded-full">
                  {activeNetworks.length} selected
                </span>
              </div>

              {activeNetworks.length > 0 ? (
                <div className="space-y-2 mb-4">
                  {activeNetworks.map(network => (
                    <div
                      key={network.id}
                      className="flex items-center justify-between p-3 bg-brand-light-background dark:bg-brand-dark-background rounded-lg border border-brand-light-card-border dark:border-brand-dark-card-border"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 bg-brand-accent rounded-full"></div>
                        <span className="text-sm font-medium text-brand-light-text-default dark:text-brand-dark-text-default">
                          {network.name}
                        </span>
                      </div>
                      <button
                        className="text-brand-light-text-subtle-description hover:text-brand-light-red dark:hover:text-brand-dark-red transition-colors p-1 rounded"
                        onClick={() => {
                          const existingActiveNetworkIndex =
                            activeNetworks.findIndex(x => x.id === network.id)
                          if (existingActiveNetworkIndex !== -1) {
                            setActiveNetworks(networks => {
                              const temp = [...networks]
                              temp.splice(existingActiveNetworkIndex, 1)
                              return temp
                            })
                          }
                        }}
                      >
                        <FontAwesomeIcon icon={faXmark} className="text-xs" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-brand-light-text-subtle-description dark:text-brand-dark-text-subtle-description">
                  <p className="text-sm">No networks selected</p>
                </div>
              )}

              <CDropdownMenu
                emptyText="Select networks to support"
                label="Add Networks"
                selectedIconSrc={null}
                selectedText={undefined}
                renderList={networks?.map(network => {
                  return (
                    <DropdownCell
                      key={network.id}
                      title={network.name}
                      subtitle={network.nativeCurrency?.isoCode}
                      onClick={() => {
                        const existingActiveNetworkIndex =
                          activeNetworks.findIndex(x => x.id === network.id)
                        if (existingActiveNetworkIndex !== -1) {
                          setActiveNetworks(networks => {
                            const temp = [...networks]
                            temp.splice(existingActiveNetworkIndex, 1)
                            return temp
                          })
                        } else {
                          setActiveNetworks(networks => [...networks, network])
                        }
                      }}
                      isActive={false}
                      imageName={network}
                    />
                  )
                })}
              />
            </div>

            {/* Inbound Wallets Section */}
            <div className="card">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-base font-semibold text-brand-light-text-default dark:text-brand-dark-text-default">
                  Inbound Wallets
                </h3>
                <span className="text-xs text-brand-light-text-subtle-description dark:text-brand-dark-text-subtle-description bg-brand-light-card-border dark:bg-brand-dark-card-border px-2 py-1 rounded-full">
                  {activeWallets.length} selected
                </span>
              </div>

              {activeWallets.length > 0 ? (
                <div className="space-y-2 mb-4">
                  {activeWallets.map(wallet => (
                    <div
                      key={wallet.id}
                      className="flex items-center justify-between p-3 bg-brand-light-background dark:bg-brand-dark-background rounded-lg border border-brand-light-card-border dark:border-brand-dark-card-border"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 bg-brand-accent rounded-full"></div>
                        <div>
                          <p className="text-sm font-medium text-brand-light-text-default dark:text-brand-dark-text-default">
                            {'name' in wallet && wallet.name
                              ? (wallet as any).name
                              : `${wallet.address.slice(0, 8)}...${wallet.address.slice(-6)}`}
                          </p>
                          {'name' in wallet && (wallet as any).name && (
                            <p className="text-xs text-brand-light-text-subtle-description dark:text-brand-dark-text-subtle-description">
                              {`${wallet.address.slice(0, 8)}...${wallet.address.slice(-6)}`}
                            </p>
                          )}
                          <p className="text-xs text-brand-light-text-subtle-description dark:text-brand-dark-text-subtle-description">
                            {wallet.network.name}
                          </p>
                        </div>
                      </div>
                      <button
                        className="text-brand-light-text-subtle-description hover:text-brand-light-red dark:hover:text-brand-dark-red transition-colors p-1 rounded"
                        onClick={() => {
                          const existingActiveWalletIndex =
                            activeWallets.findIndex(x => x.id === wallet.id)
                          if (existingActiveWalletIndex !== -1) {
                            setActiveWallets(wallets => {
                              const temp = [...wallets]
                              temp.splice(existingActiveWalletIndex, 1)
                              return temp
                            })
                          }
                        }}
                      >
                        <FontAwesomeIcon icon={faXmark} className="text-xs" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-brand-light-text-subtle-description dark:text-brand-dark-text-subtle-description">
                  <p className="text-sm">No wallets selected</p>
                </div>
              )}

              <CDropdownMenu
                emptyText="Select wallets to support"
                label="Add Wallets"
                tooltip="Where investors send money to"
                selectedIconSrc={null}
                selectedText={undefined}
                renderList={[...(wallets ?? [])]
                  ?.sort((a, b) => {
                    const aDisabled = !activeNetworks.find(
                      x => x.id === a.network.id
                    )
                    const bDisabled = !activeNetworks.find(
                      x => x.id === b.network.id
                    )
                    if (aDisabled && !bDisabled) return 1
                    else if (!aDisabled && bDisabled) return -1
                    else return 0
                  })
                  .map(wallet => {
                    return (
                      <DropdownCell
                        key={wallet.id}
                        disabled={
                          !activeNetworks.find(x => x.id === wallet.network.id)
                        }
                        title={
                          'name' in wallet && wallet.name
                            ? (wallet as any).name
                            : wallet.address
                        }
                        subtitle={
                          'name' in wallet && (wallet as any).name
                            ? `${wallet.address} • ${wallet.network.name}`
                            : wallet.network.name
                        }
                        onClick={() => {
                          const existingActiveWalletIndex =
                            activeWallets.findIndex(x => x.id === wallet.id)
                          if (existingActiveWalletIndex !== -1) {
                            setActiveWallets(wallets => {
                              const temp = [...wallets]
                              temp.splice(existingActiveWalletIndex, 1)
                              return temp
                            })
                          } else {
                            setActiveWallets(wallets => [...wallets, wallet])
                          }
                        }}
                        isActive={false}
                      />
                    )
                  })}
              />
            </div>

            {/* Outbound Wallets Section */}
            <div className="card">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-base font-semibold text-brand-light-text-default dark:text-brand-dark-text-default">
                  Outbound Wallets
                </h3>
                <span className="text-xs text-brand-light-text-subtle-description dark:text-brand-dark-text-subtle-description bg-brand-light-card-border dark:bg-brand-dark-card-border px-2 py-1 rounded-full">
                  {activeOutboundWallets.length} selected
                </span>
              </div>

              {activeOutboundWallets.length > 0 ? (
                <div className="space-y-2 mb-4">
                  {activeOutboundWallets.map(wallet => (
                    <div
                      key={wallet.id}
                      className="flex items-center justify-between p-3 bg-brand-light-background dark:bg-brand-dark-background rounded-lg border border-brand-light-card-border dark:border-brand-dark-card-border"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 bg-brand-accent rounded-full"></div>
                        <div>
                          <p className="text-sm font-medium text-brand-light-text-default dark:text-brand-dark-text-default">
                            {'name' in wallet && wallet.name
                              ? (wallet as any).name
                              : `${wallet.address.slice(0, 8)}...${wallet.address.slice(-6)}`}
                          </p>
                          {'name' in wallet && (wallet as any).name && (
                            <p className="text-xs text-brand-light-text-subtle-description dark:text-brand-dark-text-subtle-description">
                              {`${wallet.address.slice(0, 8)}...${wallet.address.slice(-6)}`}
                            </p>
                          )}
                          <p className="text-xs text-brand-light-text-subtle-description dark:text-brand-dark-text-subtle-description">
                            {wallet.network.name}
                          </p>
                        </div>
                      </div>
                      <button
                        className="text-brand-light-text-subtle-description hover:text-brand-light-red dark:hover:text-brand-dark-red transition-colors p-1 rounded"
                        onClick={() => {
                          const existingActiveWalletIndex =
                            activeOutboundWallets.findIndex(
                              x => x.id === wallet.id
                            )
                          if (existingActiveWalletIndex !== -1) {
                            setActiveOutboundWallets(wallets => {
                              const temp = [...wallets]
                              temp.splice(existingActiveWalletIndex, 1)
                              return temp
                            })
                          }
                        }}
                      >
                        <FontAwesomeIcon icon={faXmark} className="text-xs" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-brand-light-text-subtle-description dark:text-brand-dark-text-subtle-description">
                  <p className="text-sm">No outbound wallets selected</p>
                </div>
              )}

              <CDropdownMenu
                emptyText="Select outbound wallets"
                label="Add Outbound Wallets"
                tooltip="Where we send money from (for withdrawals)"
                selectedIconSrc={null}
                selectedText={undefined}
                renderList={[...(wallets ?? [])]
                  ?.sort((a, b) => {
                    const aDisabled = !activeNetworks.find(
                      x => x.id === a.network.id
                    )
                    const bDisabled = !activeNetworks.find(
                      x => x.id === b.network.id
                    )
                    if (aDisabled && !bDisabled) return 1
                    else if (!aDisabled && bDisabled) return -1
                    else return 0
                  })
                  .map(wallet => {
                    return (
                      <DropdownCell
                        key={wallet.id}
                        disabled={
                          !activeNetworks.find(x => x.id === wallet.network.id)
                        }
                        title={
                          'name' in wallet && wallet.name
                            ? (wallet as any).name
                            : wallet.address
                        }
                        subtitle={
                          'name' in wallet && (wallet as any).name
                            ? `${wallet.address} • ${wallet.network.name}`
                            : wallet.network.name
                        }
                        onClick={() => {
                          const existingActiveWalletIndex =
                            activeOutboundWallets.findIndex(
                              x => x.id === wallet.id
                            )
                          if (existingActiveWalletIndex !== -1) {
                            setActiveOutboundWallets(wallets => {
                              const temp = [...wallets]
                              temp.splice(existingActiveWalletIndex, 1)
                              return temp
                            })
                          } else {
                            setActiveOutboundWallets(wallets => [
                              ...wallets,
                              wallet,
                            ])
                          }
                        }}
                        isActive={false}
                      />
                    )
                  })}
              />
            </div>
          </div>
        )}

        {/* Inbound Bank Account Section for Fiat Currencies */}
        {currency?.currencyType === 'FiatCurrency' && (
          <div className="card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold text-brand-light-text-default dark:text-brand-dark-text-default">
                Inbound Bank Account
              </h3>
            </div>

            <CDropdownMenu
              emptyText="Select inbound bank account"
              label="Select Inbound Bank Account"
              tooltip="Where investors send money to"
              selectedIconSrc={null}
              selectedText={selectedSystemBankAccount?.name}
              renderList={bankAccounts?.map(bankAccount => {
                return (
                  <DropdownCell
                    key={bankAccount.id}
                    title={bankAccount.name}
                    subtitle={bankAccount.description || ''}
                    onClick={() => {
                      if (
                        selectedSystemBankAccount &&
                        bankAccount.id === selectedSystemBankAccount.id
                      ) {
                        setSelectedSystemBankAccount(null)
                      } else {
                        setSelectedSystemBankAccount(bankAccount)
                      }
                    }}
                    isActive={selectedSystemBankAccount?.id === bankAccount.id}
                  />
                )
              })}
            />
          </div>
        )}

        {/* Outbound Bank Account Section for Fiat Currencies */}
        {currency?.currencyType === 'FiatCurrency' && (
          <div className="card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold text-brand-light-text-default dark:text-brand-dark-text-default">
                Outbound Bank Account
              </h3>
            </div>

            <CDropdownMenu
              emptyText="Select outbound bank account"
              label="Select Outbound Bank Account"
              tooltip="Where we send money from (for withdrawals)"
              selectedIconSrc={null}
              selectedText={selectedOutboundBankAccount?.name}
              renderList={bankAccounts?.map(bankAccount => {
                return (
                  <DropdownCell
                    key={bankAccount.id}
                    title={bankAccount.name}
                    subtitle={bankAccount.description || ''}
                    onClick={() => {
                      if (
                        selectedOutboundBankAccount &&
                        bankAccount.id === selectedOutboundBankAccount.id
                      ) {
                        setSelectedOutboundBankAccount(null)
                      } else {
                        setSelectedOutboundBankAccount(bankAccount)
                      }
                    }}
                    isActive={
                      selectedOutboundBankAccount?.id === bankAccount.id
                    }
                  />
                )
              })}
            />
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 pt-4 border-t border-brand-light-card-border dark:border-brand-dark-card-border">
          <button className="button-secondary flex-1" onClick={onClose}>
            Cancel
          </button>
          <button
            className="button flex-1"
            disabled={
              updateCurrencyIsLoading ||
              (currency?.currencyType === 'CryptoCurrency' &&
                (activeNetworks.length === 0 ||
                  activeWallets.length === 0 ||
                  activeNetworks.length !== activeWallets.length))
            }
            onClick={async () => {
              onClose()
              if (!currency) return
              try {
                await updateCurrency({
                  id: currency.id,
                  body: {
                    active,
                    networkIds: activeNetworks.map(x => x.id),
                    supportedSystemWalletIds: activeWallets.map(x => x.id),
                    outboundSystemWalletIds: activeOutboundWallets.map(
                      x => x.id
                    ),
                    systemBankAccountId: selectedSystemBankAccount?.id,
                    outboundSystemBankAccountId:
                      selectedOutboundBankAccount?.id,
                  },
                }).unwrap()
                toast({
                  position: 'top',
                  render: () => {
                    return (
                      <Toast type="success">
                        Currency settings updated successfully.
                      </Toast>
                    )
                  },
                })
              } catch (e) {
                captureException(e)
                toast({
                  position: 'top',
                  render: () => {
                    return (
                      <Toast type="error">
                        Failed to update currency settings.
                      </Toast>
                    )
                  },
                })
              }
            }}
          >
            {updateCurrencyIsLoading ? (
              <div className="flex items-center gap-2">
                <Spinner size="xs" />
                Updating...
              </div>
            ) : (
              'Save Changes'
            )}
          </button>
        </div>
      </div>
    </DefaultModal>
  )
}

export default CurrencyOverviewDetailsModal
